import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Phone, Menu, X } from 'lucide-react';
import { useTenant } from '@/contexts/TenantContext';
import { LanguageSelector } from '@/components/shared/LanguageSelector';
import { cn } from '@/lib/utils';

interface NavItem {
  labelKey: string;
  href: string;
  isAnchor?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { labelKey: 'landing.nav.home', href: '/' },
  { labelKey: 'landing.nav.routes', href: '#popular-routes', isAnchor: true },
  { labelKey: 'landing.nav.about', href: '#about', isAnchor: true },
  { labelKey: 'landing.nav.contact', href: '#contact', isAnchor: true },
];

/**
 * EnhancedHeader - Static white navigation header bar
 *
 * Features:
 * - White background bar above hero (not overlaying)
 * - Desktop: Logo (left), nav menu, spacer, phone numbers, language toggle (right)
 * - Mobile: Hamburger menu, centered logo, language toggle
 * - Mobile menu includes nav items + phone numbers
 * - 64px height
 */
export function EnhancedHeader() {
  const { t } = useTranslation();
  const { tenant } = useTenant();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Close mobile menu on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileMenuOpen(false);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  // Handle navigation clicks with smooth scroll for anchors
  const handleNavClick = (href: string, isAnchor?: boolean) => {
    if (isAnchor) {
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
    setMobileMenuOpen(false);
  };

  return (
    <>
      <header className="relative z-50 h-16 bg-white border-b hairline">
        <div className="mx-auto max-w-6xl px-6 h-full">
          <div className="flex items-center justify-between h-full">
            {/* Mobile: Hamburger Menu */}
            <button
              type="button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 -ml-2 text-ink hover:bg-stone-100 transition-colors focus-visible-ring"
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? (
                <X className="h-5 w-5" />
              ) : (
                <Menu className="h-5 w-5" />
              )}
            </button>

            {/* Wordmark — editorial, not decorative */}
            <a
              href="/"
              className="flex items-center gap-2.5 lg:flex-none absolute lg:relative left-1/2 lg:left-auto -translate-x-1/2 lg:translate-x-0 text-ink hover:opacity-80 transition-opacity"
            >
              <span className="h-2 w-2 bg-brand" aria-hidden="true" />
              <span className="font-display text-[1.25rem] lg:text-[1.4rem] tracking-tight text-ink">
                {tenant?.name || 'RutaLink'}
              </span>
            </a>

            {/* Desktop Navigation */}
            <nav
              className="hidden lg:flex items-center gap-7 ml-10"
              aria-label="Main navigation"
            >
              {NAV_ITEMS.map((item) => (
                <a
                  key={item.labelKey}
                  href={item.href}
                  onClick={(e) => {
                    if (item.isAnchor) {
                      e.preventDefault();
                      handleNavClick(item.href, item.isAnchor);
                    }
                  }}
                  className="text-[0.85rem] font-medium text-ink-muted hover:text-ink transition-colors"
                >
                  {t(item.labelKey)}
                </a>
              ))}
            </nav>

            <div className="hidden lg:flex flex-1" />

            {/* Desktop: Phone Numbers */}
            <div className="hidden lg:flex items-center gap-5 mr-5">
              {tenant?.contact?.phoneUs && (
                <a
                  href={`tel:${tenant.contact.phoneUs.replace(/[^+\d]/g, '')}`}
                  className="flex items-center gap-2 text-[0.85rem] text-ink-muted hover:text-ink transition-colors tabular"
                >
                  <Phone className="h-3.5 w-3.5" aria-hidden="true" />
                  <span className="text-eyebrow uppercase text-ink-subtle">US</span>
                  <span>{tenant.contact.phoneUs}</span>
                </a>
              )}
              {tenant?.contact?.phoneMx && (
                <a
                  href={`tel:${tenant.contact.phoneMx.replace(/[^+\d]/g, '')}`}
                  className="flex items-center gap-2 text-[0.85rem] text-ink-muted hover:text-ink transition-colors tabular"
                >
                  <Phone className="h-3.5 w-3.5" aria-hidden="true" />
                  <span className="text-eyebrow uppercase text-ink-subtle">MX</span>
                  <span>{tenant.contact.phoneMx}</span>
                </a>
              )}
            </div>

            <div className="[&_button]:text-ink-muted [&_button]:hover:text-ink">
              <LanguageSelector variant="compact" />
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Mobile Menu Panel */}
      <nav
        className={cn(
          'absolute top-16 left-0 right-0 z-40 lg:hidden transition-all duration-300 overflow-hidden bg-white border-b hairline',
          mobileMenuOpen ? 'max-h-[28rem] opacity-100' : 'max-h-0 opacity-0'
        )}
        aria-label="Mobile navigation"
      >
        <div className="max-w-6xl mx-auto px-6 py-5 divide-y divide-hairline">
          {NAV_ITEMS.map((item) => (
            <a
              key={item.labelKey}
              href={item.href}
              onClick={(e) => {
                if (item.isAnchor) {
                  e.preventDefault();
                }
                handleNavClick(item.href, item.isAnchor);
              }}
              className="flex items-center py-3 text-ink hover:text-brand transition-colors"
            >
              <span className="font-medium">{t(item.labelKey)}</span>
            </a>
          ))}

          {tenant?.contact?.phoneUs && (
            <a
              href={`tel:${tenant.contact.phoneUs.replace(/[^+\d]/g, '')}`}
              className="flex items-center gap-3 py-3 text-ink hover:text-brand transition-colors"
            >
              <Phone className="h-4 w-4 text-ink-muted" aria-hidden="true" />
              <div>
                <span className="block text-eyebrow uppercase text-ink-subtle">USA</span>
                <span className="font-medium tabular">{tenant.contact.phoneUs}</span>
              </div>
            </a>
          )}

          {tenant?.contact?.phoneMx && (
            <a
              href={`tel:${tenant.contact.phoneMx.replace(/[^+\d]/g, '')}`}
              className="flex items-center gap-3 py-3 text-ink hover:text-brand transition-colors"
            >
              <Phone className="h-4 w-4 text-ink-muted" aria-hidden="true" />
              <div>
                <span className="block text-eyebrow uppercase text-ink-subtle">Mexico</span>
                <span className="font-medium tabular">{tenant.contact.phoneMx}</span>
              </div>
            </a>
          )}
        </div>
      </nav>
    </>
  );
}

export default EnhancedHeader;
